import { z } from "zod";
import { TimestampField, ErrorResponse } from "./common";

/**
 * Exhaustive list of Casdoor User struct field names valid for use in
 * Application.tokenFields — controls which claims are embedded in issued JWTs.
 * Derived from: https://casdoor.org/docs/basic/core-concepts#user
 */
// These must match Go struct field names exactly (PascalCase) as Casdoor uses
// reflect.FieldByName() which is case-sensitive.
export const CASDOOR_TOKEN_FIELDS = [
  "Id",
  "Owner",
  "Name",
  "Type",
  "DisplayName",
  "FirstName",
  "LastName",
  "Avatar",
  "PermanentAvatar",
  "Email",
  "Phone",
  "Location",
  "Address",
  "Affiliation",
  "Title",
  "IdCardType",
  "IdCard",
  "Homepage",
  "Bio",
  "Tag",
  "Region",
  "Language",
  "Gender",
  "Birthday",
  "Education",
  "Score",
  "Karma",
  "Ranking",
  "IsDefaultAvatar",
  "IsOnline",
  "IsAdmin",
  "IsGlobalAdmin",
  "IsForbidden",
  "IsDeleted",
  "SignupApplication",
  "CreatedIp",
  "LastSigninTime",
  "LastSigninIp",
  "GitHub",
  "Google",
  "QQ",
  "WeChat",
  "Facebook",
  "DingTalk",
  "Weibo",
  "Gitee",
  "LinkedIn",
  "Wecom",
  "Lark",
  "Gitlab",
  "permissionNames",
  "Properties.workspaces",
] as const;

export type CasdoorTokenField = (typeof CASDOOR_TOKEN_FIELDS)[number];

/**
 * Canonical keys for the Casdoor User.properties map.
 * `workspaces` is stored as a JSON-encoded string array (e.g. '["uuid1","uuid2"]')
 * and surfaces as the `workspaces` JWT claim via `Properties.workspaces` in tokenFields.
 */
export const USER_PROPERTY_KEYS = ["role", "workspaces", "tenantId"] as const;

export type UserPropertyKey = (typeof USER_PROPERTY_KEYS)[number];

const LoginUrlResponse = z.object({
  success: z.literal(true),
  data: z.object({ authUrl: z.url(), state: z.string() }),
});
const CallbackRequest = z.object({ code: z.string(), state: z.string() });
const CasdoorUser = z.object({
  owner: z.string(),
  name: z.string(),
  createdTime: TimestampField,
  updatedTime: TimestampField,
  id: z.string(),
  type: z.string(),
  password: z.string().optional(),
  passwordSalt: z.string().optional(),
  displayName: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  avatar: z.url().optional(),
  permanentAvatar: z.url().optional(),
  email: z.email(),
  phone: z.string().optional(),
  location: z.string().optional(),
  address: z.array(z.string()).optional(),
  affiliation: z.string().optional(),
  title: z.string().optional(),
  idCardType: z.string().optional(),
  idCard: z.string().optional(),
  homepage: z.url().optional(),
  bio: z.string().optional(),
  tag: z.string().optional(),
  region: z.string().optional(),
  language: z.string().optional(),
  gender: z.string().optional(),
  birthday: z.string().optional(),
  education: z.string().optional(),
  score: z.number().int().optional(),
  karma: z.number().int().optional(),
  ranking: z.number().int().optional(),
  isDefaultAvatar: z.boolean().optional(),
  isOnline: z.boolean().optional(),
  isAdmin: z.boolean(),
  isGlobalAdmin: z.boolean(),
  isForbidden: z.boolean(),
  isDeleted: z.boolean(),
  signupApplication: z.string().optional(),
  hash: z.string().optional(),
  preHash: z.string().optional(),
  createdIp: z.string().optional(),
  lastSigninTime: TimestampField.optional(),
  lastSigninIp: z.string().optional(),
  github: z.string().optional(),
  google: z.string().optional(),
  qq: z.string().optional(),
  wechat: z.string().optional(),
  facebook: z.string().optional(),
  dingtalk: z.string().optional(),
  weibo: z.string().optional(),
  gitee: z.string().optional(),
  linkedin: z.string().optional(),
  wecom: z.string().optional(),
  lark: z.string().optional(),
  gitlab: z.string().optional(),
  properties: z.record(z.string(), z.string()).optional(),
});
const CasdoorOrganization = z.object({
  owner: z.string(),
  name: z.string(),
  createdTime: z.string().optional(),
  displayName: z.string().optional(),
  websiteUrl: z.string().optional(),
  favicon: z.string().optional(),
  passwordType: z.string().optional(),
  passwordSalt: z.string().optional(),
  phonePrefix: z.string().optional(),
  defaultAvatar: z.string().optional(),
  tags: z.array(z.string()).optional(),
  masterPassword: z.string().optional(),
  enableSoftDeletion: z.boolean().optional(),
  isProfilePublic: z.boolean().optional(),
});
const CasdoorApplication = z.object({
  owner: z.string(),
  name: z.string(),
  createdTime: z.string().optional(),
  displayName: z.string().optional(),
  logo: z.string().optional(),
  homepageUrl: z.string().optional(),
  description: z.string().optional(),
  organization: z.string(),
  cert: z.string(),
  enablePassword: z.boolean().optional(),
  enableSignUp: z.boolean().optional(),
  enableSigninSession: z.boolean().optional(),
  enableCodeSignin: z.boolean().optional(),
  clientId: z.string().optional(),
  clientSecret: z.string().optional(),
  redirectUris: z.array(z.string()).optional(),
  tokenFormat: z.enum(["JWT", "JWT-Standard"]).optional(),
  expireInHours: z.number().positive().optional(),
  refreshExpireInHours: z.number().positive().optional(),
  signupUrl: z.string().optional(),
  signinUrl: z.string().optional(),
  forgetUrl: z.string().optional(),
  affiliationUrl: z.string().optional(),
  termsOfUse: z.string().optional(),
  signupHtml: z.string().optional(),
  signinHtml: z.string().optional(),
  tokenFields: z.array(z.enum(CASDOOR_TOKEN_FIELDS)).optional(),
});
const AuthTokens = z.object({
  accessToken: z.string(),
  idToken: z.string().optional(),
  refreshToken: z.string().optional(),
  tokenType: z.string(),
  expiresIn: z.number().int(),
  scope: z.string(),
});
const AuthResponse = z.object({
  success: z.literal(true),
  data: z.object({ user: CasdoorUser, tokens: AuthTokens }),
});
const UserResponse = z.object({ success: z.literal(true), data: CasdoorUser });
const VerifyTokenRequest = z.object({ token: z.string() });
const VerifyTokenResponse = z.object({
  success: z.literal(true),
  data: z.object({ valid: z.boolean() }),
});
const RefreshTokenRequest = z.object({ refreshToken: z.string() });
const TokenResponse = z.object({ success: z.literal(true), data: AuthTokens });
const LogoutRequest = z.object({ token: z.string() }).partial();
const LogoutResponse = z.object({
  success: z.literal(true),
  message: z.string(),
});
export const schemas = {
  LoginUrlResponse,
  ErrorResponse,
  CallbackRequest,
  CasdoorUser,
  CasdoorOrganization,
  CasdoorApplication,
  AuthTokens,
  AuthResponse,
  UserResponse,
  VerifyTokenRequest,
  VerifyTokenResponse,
  RefreshTokenRequest,
  TokenResponse,
  LogoutRequest,
  LogoutResponse,
};

// oRPC Type Inference Support
// These types enable proper oRPC client generation and type inference

export type SchemaTypes = {
  [K in keyof typeof schemas]: z.infer<(typeof schemas)[K]>;
};

// Re-export for convenient access
export type * from "zod";
